import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "moment";
import { useSnackbar } from "notistack";
import ImageViewer from "app/widgets/Images/ImageViewer";
import bugReportServices from "app/services/bug-report-services";
import { HOST } from "app/utils/constants/paths";
import { STATUS_COLORS, STATUS_LABELS, contextLabel, orderedContextEntries } from "./constants";

// ActiveStorage devuelve una ruta relativa ("/rails/active_storage/..."), así que hay que
// prefijar el host. Mismo helper que repiten SKUList y SKUEditDrawer.
const buildImageSrc = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${HOST}${url}`;
};

const BugReportDetailsDialog = ({ record, onClose = () => {}, onReviewed = () => {} }) => {
    const { enqueueSnackbar } = useSnackbar();
    const open = Boolean(record);

    const [status, setStatus] = useState("pending");
    const [internalNote, setInternalNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [openImageViewer, setOpenImageViewer] = useState(false);
    const [imagenInicial, setImagenInicial] = useState(0);

    // Se reinicia cada vez que se abre otro reporte: sin esto, la nota del anterior queda
    // escrita en el formulario del siguiente.
    useEffect(() => {
        setStatus(record?.status || "pending");
        setInternalNote(record?.internal_note || "");
    }, [record]);

    const contextEntries = useMemo(() => orderedContextEntries(record?.context), [record]);

    const screenshots = useMemo(() => (
        (record?.screenshots || []).map((screenshot) => ({ url: buildImageSrc(screenshot.url) }))
    ), [record]);

    const hasChanges = status !== (record?.status || "pending")
        || internalNote !== (record?.internal_note || "");

    const saveReview = async () => {
        if (!record) return;

        try {
            setSaving(true);
            const updated = await bugReportServices.reviewBugReport(record.id, {
                status,
                internal_note: internalNote,
            });
            enqueueSnackbar("Reporte actualizado", { variant: "success" });
            onReviewed(updated);
            onClose();
        } catch (_error) {
            // El interceptor global ya muestra el mensaje.
        } finally {
            setSaving(false);
        }
    };

    const renderContext = () => {
        if (!contextEntries.length) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Este reporte llegó sin contexto.
                </Typography>
            );
        }

        return (
            <Table size="small">
                <TableBody>
                    {contextEntries.map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell sx={{ width: 190, color: "text.secondary", border: 0, py: 0.5, pl: 0 }}>
                                {contextLabel(key)}
                            </TableCell>
                            <TableCell sx={{ border: 0, py: 0.5, overflowWrap: "anywhere" }}>
                                {String(value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    return (
        <React.Fragment>
            <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="h2" color="primary">
                            Reporte Nº{record?.id}
                        </Typography>
                        <Chip
                            size="small"
                            label={STATUS_LABELS[record?.status] || record?.status}
                            color={STATUS_COLORS[record?.status] || "default"}
                        />
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    <Typography variant="h4">Lo que reportó</Typography>
                    <Box sx={{ p: 2, mb: 3, bgcolor: "action.hover", borderRadius: 2, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                        {record?.message}
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Typography variant="h4">Contexto</Typography>
                            {renderContext()}
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Typography variant="h4">Quién reportó</Typography>
                            <Stack sx={{ mb: 3 }} spacing={0.5}>
                                <Typography>{record?.reporter?.name || "-"}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {record?.reporter?.email || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {record?.company_name || "Sin empresa"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {record?.created_at ? moment(record.created_at).format("DD/MM/YYYY HH:mm") : "-"}
                                </Typography>
                            </Stack>

                            <Typography variant="h4">Capturas</Typography>
                            {screenshots.length ? (
                                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                    {screenshots.map((screenshot, index) => (
                                        <Box
                                            key={screenshot.url}
                                            component="img"
                                            src={screenshot.url}
                                            alt={`Captura ${index + 1}`}
                                            onClick={() => { setImagenInicial(index); setOpenImageViewer(true); }}
                                            sx={{
                                                width: 72,
                                                height: 72,
                                                objectFit: "cover",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                border: 1,
                                                borderColor: "divider",
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No adjuntó capturas.
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h4">Revisión del equipo</Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Estado"
                                value={status}
                                onChange={(event) => setStatus(event.target.value)}
                            >
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                label="Nota interna"
                                placeholder="Qué se encontró, qué se hizo, por qué se cierra"
                                value={internalNote}
                                onChange={(event) => setInternalNote(event.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {record?.reviewed_by && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            Última revisión: {record.reviewed_by.name}
                            {record.reviewed_at ? ` · ${moment(record.reviewed_at).format("DD/MM/YYYY HH:mm")}` : ""}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Cerrar</Button>
                    <LoadingButton
                        variant="contained"
                        loading={saving}
                        disabled={!hasChanges}
                        onClick={saveReview}
                    >
                        Guardar revisión
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <ImageViewer
                isOpen={openImageViewer}
                onClose={() => setOpenImageViewer(false)}
                title={`Capturas del reporte Nº${record?.id}`}
                images={screenshots}
                startAt={imagenInicial}
            />
        </React.Fragment>
    );
};

export default BugReportDetailsDialog;
